import React, {useState, useEffect, useRef} from 'react'
import {Modal} from 'react-bootstrap'
import Button from './Button'

let WebViewer: any = null
if (typeof window !== 'undefined') {
  import('@pdftron/pdfjs-express-viewer').then((module) => {
    WebViewer = module.default
  })
}
interface WebViewerInstance {
  dispose: () => void
  Core: any
  UI: any
}

interface WebViewerOptions {
  path: string
  initialDoc: string
  enableAnnotations?: boolean
  licenseKey?: string
  disabledElements?: string[]
  customCss?: string
  initialZoom?: number
}

interface DocumentViewerModalProps {
  show: boolean
  onClose: () => void
  file: {
    url: string
    name: string
  } | null
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({show, onClose, file}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [docxLoadAttempt, setDocxLoadAttempt] = useState(0)
  const [sidebarWasCollapsed, setSidebarWasCollapsed] = useState(false)
  const [imageZoom, setImageZoom] = useState(1)
  const [imagePosition, setImagePosition] = useState({x: 0, y: 0})
  const viewerRef = useRef<HTMLDivElement>(null)
  const webViewerInstance = useRef<WebViewerInstance | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const minimizeSidebar = () => {
    const sidebar = document.getElementById('kt_app_sidebar')
    const toggleButton = document.getElementById('kt_app_sidebar_toggle')
    if (sidebar && toggleButton) {
      const isMinimized = document.body.getAttribute('data-kt-app-sidebar-minimize') === 'on'
      const isCollapsed = document.body.getAttribute('data-kt-app-sidebar-collapse') === 'on'
      setSidebarWasCollapsed(isMinimized || isCollapsed)

      if (!isMinimized && !isCollapsed) {
        toggleButton.click()
      }
    }
  }

  const restoreSidebar = () => {
    const toggleButton = document.getElementById('kt_app_sidebar_toggle')
    if (toggleButton && !sidebarWasCollapsed) {
      const isMinimized = document.body.getAttribute('data-kt-app-sidebar-minimize') === 'on'
      const isCollapsed = document.body.getAttribute('data-kt-app-sidebar-collapse') === 'on'
      if (isMinimized || isCollapsed) {
        toggleButton.click()
      }
    }
  }

  useEffect(() => {
    if (show && file) {
      setIsLoading(true)
      setError(null)
      setDocxLoadAttempt(0)
      setImageZoom(1)
      setImagePosition({x: 0, y: 0})
      minimizeSidebar()

      const fileExtension = getFileExtension(file.name, file.url)
      if (fileExtension !== 'docx') {
        const timeoutDuration = fileExtension === 'docx' ? 5000 : 2000
        const timer = setTimeout(() => {
          setIsLoading(false)
        }, timeoutDuration)

        return () => {
          clearTimeout(timer)
          restoreSidebar()
        }
      } else {
        const docxTimer = setTimeout(() => {
          if (isLoading) {
            if (iframeRef.current) {
              try {
                const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document
                if (iframeDoc && iframeDoc.body) {
                  const hasContent = iframeDoc.body.children.length > 0 || iframeDoc.body.innerHTML.trim().length > 0
                  if (hasContent) {
                    setIsLoading(false)
                    setError(null)
                    return
                  }
                }
              } catch (error) {
                setIsLoading(false)
                setError(null)
                return
              }
            }
            setIsLoading(false)
            setError('Document took too long to load')
          }
        }, 15000)

        return () => {
          clearTimeout(docxTimer)
          restoreSidebar()
        }
      }
    } else if (!show) {
      restoreSidebar()
    }
  }, [show, file])

  useEffect(() => {
    if (show && file && getFileExtension(file.name, file.url) === 'pdf' && !webViewerInstance.current) {
      const initWebViewer = async () => {
        try {
          setIsLoading(true)
          setError(null)

          let attempts = 0
          while (!viewerRef.current && attempts < 50) {
            await new Promise((resolve) => setTimeout(resolve, 100))
            attempts++
          }

          if (!viewerRef.current) {
            throw new Error('Viewer element not found after waiting')
          }

          if (!WebViewer) {
            await new Promise((resolve) => {
              const checkWebViewer = () => {
                if (WebViewer) {
                  resolve(true)
                } else {
                  setTimeout(checkWebViewer, 100)
                }
              }
              checkWebViewer()
            })
          }

          if (viewerRef.current) {
            viewerRef.current.innerHTML = ''
          }

          const options: WebViewerOptions = {
            path: '/webviewer/lib',
            initialDoc: file.url,
            enableAnnotations: false,
            licenseKey: process.env.REACT_APP_PDFJS_EXPRESS_VIEWER_LICENSE,
            disabledElements: ['toolsButton', 'menuButton', 'watermark'],
          }

          const instance = (await WebViewer(options, viewerRef.current)) as WebViewerInstance

          webViewerInstance.current = instance

          if (instance.UI && instance.UI.setZoomLevel) {
            instance.UI.setZoomLevel(1.25)
          }

          setIsLoading(false)
        } catch (err) {
          setError('Failed to load PDF document')
          setIsLoading(false)
        }
      }

      const timer = setTimeout(() => {
        initWebViewer()
      }, 200)

      return () => {
        clearTimeout(timer)
      }
    }

    return () => {
      if (webViewerInstance.current) {
        try {
          webViewerInstance.current.dispose()
        } catch (err) {
          console.error('Error disposing WebViewer:', err)
        }
        webViewerInstance.current = null
      }
    }
  }, [show, file])

  const handleDocumentLoad = () => {
    setIsLoading(false)
  }

  const handleDocumentError = (error: any) => {
    setIsLoading(false)
    setError('Failed to load document')
  }

  const handleClose = () => {
    restoreSidebar()
    onClose()
  }

  const handleImageZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 0.25, 3))
  }

  const handleImageZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 0.25, 0.25))
  }

  const handleImageReset = () => {
    setImageZoom(1)
    setImagePosition({x: 0, y: 0})
  }

  const handleImageMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      const startX = e.clientX - imagePosition.x
      const startY = e.clientY - imagePosition.y

      const handleMouseMove = (e: MouseEvent) => {
        setImagePosition({
          x: e.clientX - startX,
          y: e.clientY - startY,
        })
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
  }

  const getFileExtension = (fileName: string, fileUrl?: string) => {
    if (fileUrl) {
      try {
        const url = new URL(fileUrl)
        const pathname = url.pathname
        const urlExtension = pathname.split('.').pop()?.toLowerCase()
        if (urlExtension && ['pdf', 'docx', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(urlExtension)) {
          return urlExtension
        }
      } catch (e) {
      }
    }
    
    return fileName.split('.').pop()?.toLowerCase() || ''
  }

  const renderDocumentViewer = () => {
    if (!file) return null

    const fileExtension = getFileExtension(file.name, file.url)

    if (fileExtension === 'pdf') {
      return (
        <div
          style={{height: '70vh', width: '100%', border: '1px solid #ccc', position: 'relative'}}
        >
          <div
            ref={viewerRef}
            style={{
              height: '100%',
              width: '100%',
              minHeight: '400px',
              backgroundColor: '#f5f5f5',
            }}
            id='pdf-viewer-container'
          />
        </div>
      )
    } else if (fileExtension === 'docx') {
      const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        file.url
      )}&embedded=true&rm=minimal&a=bi&pagenumber=1&w=1600&h=900`

      const handleDocxError = () => {
        if (docxLoadAttempt < 2) {
          setDocxLoadAttempt((prev) => prev + 1)
          if (iframeRef.current) {
            iframeRef.current.src = googleDocsUrl
          }
        } else {
          if (iframeRef.current) {
            try {
              const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document
              if (iframeDoc && iframeDoc.body) {
                const hasContent = iframeDoc.body.children.length > 0 || iframeDoc.body.innerHTML.trim().length > 0
                if (!hasContent) {
                  handleDocumentError(new Error('Document loaded but content is empty'))
                  return
                }
              }
            } catch (error) {
            }
          }
          handleDocumentError(new Error('Failed to load DOCX after multiple attempts'))
        }
      }

      const handleDocxLoad = () => {
        setIsLoading(false)
        setError(null)
        
        setTimeout(() => {
          if (iframeRef.current) {
            try {
              const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document
              if (iframeDoc && iframeDoc.body) {
                const hasContent = iframeDoc.body.children.length > 0 || iframeDoc.body.innerHTML.trim().length > 0
                if (!hasContent) {
                  handleDocxError()
                }
              }
            } catch (error) {

            }
          }
        }, 500) 
      }

      return (
        <div style={{height: '70vh', width: '100%', position: 'relative'}}>
          <iframe
            ref={iframeRef}
            src={googleDocsUrl}
            style={{width: '100%', height: '100%', border: '1px solid #ccc'}}
            title={file.name}
            onLoad={handleDocxLoad}
            onError={handleDocxError}
            allowFullScreen
            sandbox='allow-scripts allow-same-origin allow-forms'
          />
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                className='spinner-border'
                role='status'
                style={{width: '20px', height: '20px', color: '#5A5A5A'}}
              >
                <span className='visually-hidden'>Loading document...</span>
              </div>
              <div className='text-muted'>
                {docxLoadAttempt > 0
                  ? `Retrying... (Attempt ${docxLoadAttempt + 1}/3)`
                  : 'Loading document...'}
              </div>
            </div>
          )}
          {!isLoading && !error && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 5,
              }}
            >
              <button
                className='btn btn-sm btn-outline-secondary'
                onClick={() => {
                  if (iframeRef.current) {
                    iframeRef.current.src = googleDocsUrl
                    setIsLoading(true)
                  }
                }}
                title='Reload document'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <path d='M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' />
                  <path d='M21 3v5h-5' />
                  <path d='M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' />
                  <path d='M3 21v-5h5' />
                </svg>
              </button>
            </div>
          )}
        </div>
      )
    } else if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileExtension)) {
      return (
        <div
          style={{
            height: '70vh',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <button
              onClick={handleImageZoomOut}
              className='btn btn-sm btn-outline-secondary'
              title='Zoom Out'
              disabled={imageZoom <= 0.25}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <circle cx='11' cy='11' r='8' />
                <path d='m21 21-4.35-4.35' />
                <path d='m8 11h6' />
              </svg>
            </button>
            <button
              onClick={handleImageReset}
              className='btn btn-sm btn-outline-secondary'
              title='Reset Zoom'
            >
              {Math.round(imageZoom * 100)}%
            </button>
            <button
              onClick={handleImageZoomIn}
              className='btn btn-sm btn-outline-secondary'
              title='Zoom In'
              disabled={imageZoom >= 3}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <circle cx='11' cy='11' r='8' />
                <path d='m21 21-4.35-4.35' />
                <path d='m8 11h6' />
                <path d='m11 8v6' />
              </svg>
            </button>
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              cursor: imageZoom > 1 ? 'grab' : 'default',
            }}
            onMouseDown={handleImageMouseDown}
          >
            <img
              src={file.url}
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${
                  imagePosition.y / imageZoom
                }px)`,
                transition: 'transform 0.1s ease-out',
                cursor: imageZoom > 1 ? 'grab' : 'default',
              }}
              onLoad={handleDocumentLoad}
              onError={handleDocumentError}
            />
          </div>
        </div>
      )
    } else {
      return (
        <div className='d-flex justify-content-center align-items-center' style={{height: '70vh'}}>
          <div className='text-center'>
            <div className='mb-3 text-gray-700'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='40'
                height='40'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-file-type-xls'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M14 3v4a1 1 0 0 0 1 1h4' />
                <path d='M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4' />
                <path d='M4 15l4 6' />
                <path d='M4 21l4 -6' />
                <path d='M17 20.25c0 .414 .336 .75 .75 .75h1.25a1 1 0 0 0 1 -1v-1a1 1 0 0 0 -1 -1h-1a1 1 0 0 1 -1 -1v-1a1 1 0 0 1 1 -1h1.25a.75 .75 0 0 1 .75 .75' />
                <path d='M11 15v6h3' />
              </svg>
            </div>
            <h5 className='text-gray-700'>Preview not available</h5>
            <p className='text-gray-700 mb-5'>This file type cannot be previewed in the browser.</p>
            <button
              className='btn btn-primary btn-sm'
              onClick={() => {
                if (file) {
                  const link = document.createElement('a')
                  link.href = file.url
                  link.download = file.name
                  link.target = '_blank'
                  link.rel = 'noopener noreferrer'

                  if (file.url.startsWith('http')) {
                    fetch(file.url)
                      .then((response) => response.blob())
                      .then((blob) => {
                        const url = window.URL.createObjectURL(blob)
                        link.href = url
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                      })
                      .catch(() => {
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                      })
                  } else {
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                  }
                }
              }}
            >
              Download File
            </button>
          </div>
        </div>
      )
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='xl'
      centered
      dialogClassName='modal-100w'
      style={{maxWidth: '100vw', width: '100vw'}}
    >
      <Modal.Body style={{padding: '20px', minHeight: '70vh'}}>
        {error && (
          <div
            className='d-flex justify-content-center align-items-center'
            style={{height: '70vh'}}
          >
            <div className='text-center'>
              <div className='mb-3'>
                <i className='fas fa-exclamation-triangle fs-1 text-danger'></i>
              </div>
              <h5 className='text-danger'>Error Loading Document</h5>
              <p className='text-muted'>{error}</p>
              <button className='btn btn-primary' onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        )}
        {!error && renderDocumentViewer()}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-end align-items-center px-5 py-2 bg-light gap-1'>
        <Button btnType='default' onClick={handleClose}>
          Close
        </Button>
        <Button
          btnType='primary'
          onClick={() => {
            if (file) {
              const link = document.createElement('a')
              link.href = file.url
              link.download = file.name
              link.target = '_blank'
              link.rel = 'noopener noreferrer'

              if (file.url.startsWith('http')) {
                fetch(file.url)
                  .then((response) => response.blob())
                  .then((blob) => {
                    const url = window.URL.createObjectURL(blob)
                    link.href = url
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(url)
                  })
                  .catch(() => {
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                  })
              } else {
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }
            }
          }}
          title='Download'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-download me-2'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
            <path d='M7 11l5 5l5 -5' />
            <path d='M12 4l0 12' />
          </svg>
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DocumentViewerModal
